<template>
	<datatable size="large" :headers :value="items" expandable :loading>
		<template #row-quantity_required="{ row }">
			{{ statusParam(row, 'quantity_required', 'pick') }}
		</template>

		<template #row-quantity_picked="{ row }">
			<quantity-field
				v-if="row.pick_status && row.pick_status.length > 0"
				v-model="row.pick_status[0].quantity_confirmed"
				:disabled="row.type == 'Kit' || row.type == 'Comprises Of'"
				:max="row.pick_status[0].quantity_required"
				@update:model-value="emit('updatedItems', row)" />
		</template>

		<template #row-weight="{ row }">
			{{ row.pick_status[0].weight }}
		</template>

		<template #row-notes="{ row }">
			<notes-button
				v-if="row.pick_status && row.pick_status[0]"
				tablet
				:status="row.pick_status[0]"
				@click="editItem(row)" />
		</template>

		<template #row-unique_item_number="{ row }">
			<div v-if="row.pick_status[0].has_uniques" class="w-full">
				<app-button
					:disabled="
						row.type == 'Kit' ||
						row.type == 'Comprises Of' ||
						statusParam(row, 'quantity_required', 'pick') == 0 ||
						!uniqueItems(row)
					"
					:theme="
						statusParam(row, 'item_picked', 'pick') &&
						statusParam(row, 'item_picked', 'pick').length > 0
							? 'success'
							: 'primary'
					"
					size="small"
					@click="editingUniqueItem = row">
					<font-awesome-icon
						:icon="
							statusParam(row, 'item_picked', 'pick') &&
							statusParam(row, 'item_picked', 'pick').length > 0
								? 'fa-pencil'
								: 'fa-plus'
						"
						class="text-xs cursor-pointer text-white" />
				</app-button>
			</div>
		</template>
	</datatable>

	<edit-contract-item-note
		:item="editingNote"
		area="pick"
		@updated="emit('updatedNote', editingNote)"
		@closed="editingNote = null" />

	<slideover
		title="Add Unique Identifier"
		:open="editingUniqueItem !== null"
		:actions="[{ text: 'Done', method: closeUniqueItemEditor }]"
		@close="editingUniqueItem = null">
		<app-badge v-if="hasUniqueItems && uniqueIds" :theme="badgeTheme" class="mb-3" size="small">
			<strong class="mr-2">Quantity selected:</strong>
			{{ itemsPickedCount }} of
			{{ statusParam(editingUniqueItem!, 'quantity_required', 'pick') }}
		</app-badge>
		<div
			id="unique-ids"
			ref="uniqueIds"
			class="rounded-md bg-slate-100 py-2 px-3 mb-3"
			:class="{ hidden: !statusParam(editingUniqueItem!, 'item_picked', 'pick') }"></div>
		<app-dropdown
			v-if="hasUniqueItems && uniqueIds"
			v-model="editingUniqueItem!.pick_status![0].item_picked"
			size="large"
			name="item_picked"
			class="w-full"
			badge-container="#unique-ids"
			multiple
			:max="statusParam(editingUniqueItem!, 'quantity_required', 'pick')"
			label-key=""
			value-key=""
			:options="statusParam(editingUniqueItem!, 'unique_items', 'pick')"
			>Unique Identifier</app-dropdown
		>
	</slideover>
</template>

<script lang="ts" setup>
	import type { Header } from '@/data/types/datatable'
	import { ref, computed, watchEffect } from 'vue'
	import QuantityField from '@/components/partials/tables/QuantityField.vue'
	import NotesButton from '@/components/partials/cat2/NotesButton.vue'
	import { ContractItem } from '@/data/schema/contract-item'
	import { statusParam } from '@/composables/contracts'
	import { ContractItemStatus } from '@/data/schema/contract-item-status'
	import EditContractItemNote from '@/components/partials/cat2/EditContractItemNote.vue'
	import { useMatchColumnWidths } from '@/composables/datatable'

	const itemTable = ref<any>(null)
	const kitTable = ref<any>(null)
	const uniqueIds = ref<any>(null)

	const editingNote = ref<ContractItem | null>(null)
	const editingUniqueItem = ref<ContractItem | null>(null)

	defineProps({
		items: {
			type: Array<ContractItem>,
			default: []
		},
		loading: Boolean
	})

	const hasUniqueItems = computed(() => {
		return (
			editingUniqueItem.value &&
			statusParam(editingUniqueItem.value, 'unique_items', 'pick') &&
			statusParam(editingUniqueItem.value, 'unique_items', 'pick').length > 0
		)
	})

	const badgeTheme = computed(() => {
		if (!editingUniqueItem.value) {
			return 'primary'
		}

		return itemsPickedCount.value ==
			statusParam(editingUniqueItem.value, 'quantity_required', 'pick')
			? 'success'
			: 'danger'
	})

	const itemsPickedCount = computed(() => {
		if (!editingUniqueItem.value) {
			return 0
		}

		return statusParam(editingUniqueItem.value, 'item_picked', 'pick')
			? statusParam(editingUniqueItem.value, 'item_picked', 'pick').length
			: 0
	})

	const headers: Header[] = [
		{ value: 'item_number', text: 'Item No', props: { sortable: true } },
		{ value: 'description', text: 'Item Description' },
		{ value: 'type', text: 'Type', props: { sortable: true } },
		{ value: 'quantity_required', text: 'REQ' },
		{ value: 'quantity_picked', text: 'PCK' },
		{ value: 'unique_item_number', text: 'Unique Item' },
		{ value: 'weight', text: 'Total Weight', props: { sortable: true } },
		{ value: 'notes', text: 'Notes' }
	]

	function uniqueItems(item: ContractItem) {
		return (
			statusParam(item, 'unique_items', 'pick') &&
			statusParam(item, 'unique_items', 'pick').length > 0
		)
	}

	/**
	 * Ensure a note param exists
	 * Set editing value to open slideover
	 * @param item
	 */
	function editItem(item: ContractItem) {
		if (!item.pick_status || !item.pick_status[0]) {
			item.pick_status = []
			item.pick_status[0] = new ContractItemStatus()
		}

		if (!item.pick_status[0].notes) {
			item.pick_status[0].notes = ''
		}

		editingNote.value = item
	}

	const closeUniqueItemEditor = () => {
		emit('updatedItems', editingUniqueItem.value)
		editingUniqueItem.value = null
	}

	watchEffect(() => {
		if (itemTable.value && kitTable.value) {
			useMatchColumnWidths(itemTable.value, kitTable.value, [0])
		}
	})

	const emit = defineEmits(['updatedItems', 'updatedNote'])
</script>
